import { ApplicationDetails } from '@sec-loans-ui/common/steps/loan-catalog-step/types'
import { ApplicationType } from '@sec-loans-ui/common/types'
import { assertValue } from '@shared/helpers'
import {
  IfpApplicationMetadata,
  IfpApplicationState,
  SecLoanApplicationState,
  SecLoansApplicationMetadata,
} from '@shared/sec-loans'
import { IntlShape } from 'react-intl'

export const getIfpTranslationForState = (
  formatMessage: IntlShape['formatMessage'],
  metadata: IfpApplicationMetadata,
  userSsn: string,
): string => {
  const { state, stateChangedBy, primary } = metadata
  switch (state) {
    case IfpApplicationState.Cancelled: {
      if (userSsn === stateChangedBy) {
        return formatMessage({ id: 'ifp-application-state-cancelled-by-user' })
      } else {
        return formatMessage({ id: 'ifp-application-state-cancelled-by-other-user' })
      }
    }

    case IfpApplicationState.Draft: {
      return formatMessage({ id: 'ifp-application-state-waiting-for-input' })
    }

    case IfpApplicationState.WaitingForCoDebtor: {
      const actionsNeeded = assertValue(metadata.actionsNeeded, 'metadata.actionsNeeded')
      if (actionsNeeded.includes(userSsn)) {
        return formatMessage({ id: 'ifp-application-state-waiting-for-user-approval' })
      } else if (actionsNeeded.length > 1) {
        return formatMessage(
          { id: 'ifp-application-state-waiting-for-many-other-approvals' },
          { numActionsNeeded: actionsNeeded.length },
        )
      } else {
        return formatMessage({ id: 'ifp-application-state-waiting-for-other-user-approval' })
      }
    }

    case IfpApplicationState.ReturnedByCoDebtor: {
      if (userSsn === primary) {
        return formatMessage({ id: 'ifp-application-state-waiting-for-input' })
      } else if (userSsn === stateChangedBy) {
        return formatMessage({ id: 'ifp-application-state-returned-by-user' })
      } else {
        return formatMessage({ id: 'ifp-application-state-returned-by-other-user' })
      }
    }

    case IfpApplicationState.Rejected: {
      return formatMessage({ id: 'ifp-application-state-rejected' })
    }

    case IfpApplicationState.Accepted: {
      return formatMessage({ id: 'ifp-application-state-accepted' })
    }

    case IfpApplicationState.Pending:
    case IfpApplicationState.Sending:
    case IfpApplicationState.WaitingForResend: {
      return formatMessage({ id: 'ifp-application-state-pending' })
    }
  }
}

const splitIfpApplications = (ifpApplications: IfpApplicationMetadata[]) => {
  const inProgressIfpApplications: IfpApplicationMetadata[] = []
  const completedIfpApplications: IfpApplicationMetadata[] = []
  ifpApplications.forEach((a) => {
    if ([IfpApplicationState.Cancelled, IfpApplicationState.Accepted, IfpApplicationState.Rejected].includes(a.state)) {
      completedIfpApplications.push(a)
    } else {
      inProgressIfpApplications.push(a)
    }
  })

  return { inProgressIfpApplications, completedIfpApplications }
}

const splitLoanApplications = (loanApplications: SecLoansApplicationMetadata[]) => {
  const inProgressLoanApplications: SecLoansApplicationMetadata[] = []
  const completedLoanApplications: SecLoansApplicationMetadata[] = []
  loanApplications.forEach((a) => {
    if (
      [
        SecLoanApplicationState.CancelledByPrimaryApplicant,
        SecLoanApplicationState.CancelledBySecondaryApplicant,
        SecLoanApplicationState.Accepted,
        SecLoanApplicationState.Rejected,
      ].includes(a.applicationState)
    ) {
      completedLoanApplications.push(a)
    } else {
      inProgressLoanApplications.push(a)
    }
  })
  return { inProgressLoanApplications, completedLoanApplications }
}

const mergeAndSortApplications = (
  loanApplications: SecLoansApplicationMetadata[],
  ifpApplications: IfpApplicationMetadata[],
): ApplicationDetails[] => {
  const applications: ApplicationDetails[] = []

  loanApplications.forEach((a) => {
    applications.push({
      applicationType: ApplicationType.LoanApplication,
      applicationMetadata: a,
    })
  })

  ifpApplications.forEach((a) => {
    applications.push({
      applicationType: ApplicationType.InstalmentFreePeriodApplication,
      applicationMetadata: a,
    })
  })

  return applications.sort((a, b) => a.applicationMetadata.updated - b.applicationMetadata.updated)
}

export const getApplicationDetails = (
  loanApplications: SecLoansApplicationMetadata[],
  ifpApplications: IfpApplicationMetadata[],
): { inProgressApplications: ApplicationDetails[]; completedApplications: ApplicationDetails[] } => {
  const { inProgressIfpApplications, completedIfpApplications } = splitIfpApplications(ifpApplications)
  const { inProgressLoanApplications, completedLoanApplications } = splitLoanApplications(loanApplications)

  const inProgressApplications = mergeAndSortApplications(inProgressLoanApplications, inProgressIfpApplications)
  const completedApplications = mergeAndSortApplications(completedLoanApplications, completedIfpApplications)

  return { inProgressApplications, completedApplications }
}
